import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Download, TrendingUp, Loader2, Mail, CheckCircle2 } from "lucide-react";
import PublicNavbar from "../components/layout/PublicNavbar";
import Footer from "../components/layout/Footer";
import { API_BASE } from "../lib/api";
import { formatDate } from "../lib/datetime";

/**
 * SectorReports (public)
 * ----------------------
 * Free reader-magnet page. Shows current-month sector medians and offers a
 * downloadable ivory-and-forest PDF for institutions to save/share.
 */
const FOREST = "#0B2E24";
const IVORY = "#F5F1E8";
const IVORY_2 = "#EDE7D8";
const LIME = "#C4F751";

export default function SectorReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gateOpen, setGateOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", organization: "", role: "", requested_month: null });
  const [submitting, setSubmitting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/public/sector-reports`, { credentials: "omit" })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error("failed")))
      .then((d) => setData(d))
      .catch(() => toast.error("Unable to load sector reports"))
      .finally(() => setLoading(false));
  }, []);

  const submitGate = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/public/sector-reports/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify({
          email: form.email, name: form.name, organization: form.organization,
          role: form.role, marketing_opt_in: true,
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.detail || "Request failed");
      // If founder requested a past month, append ?month=YYYY-MM to the URL.
      let dl = body.download_url;
      if (form.requested_month) {
        dl += (dl.includes("?") ? "&" : "?") + `month=${form.requested_month}`;
      }
      setDownloadUrl(dl);
      toast.success(body.message || "Report emailed. You can also download it now.");
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const monthLabel = data?.month ? new Date(`${data.month}-01`).toLocaleDateString(undefined, { month: "long", year: "numeric" }) : "";

  return (
    <div style={{ background: IVORY, color: FOREST, minHeight: "100vh" }} data-testid="sector-reports-page">
      <PublicNavbar />
      <Toaster richColors position="top-right" />

      {/* Header */}
      <section
        className="py-20 lg:py-28 border-b"
        style={{ background: IVORY, borderColor: "rgba(11,46,36,0.08)" }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#0B2E24]/70">
              VENTURAZ · SECTOR HEALTH REPORT
            </div>
            <h1 className="font-editorial mt-5 text-[46px] lg:text-[68px] leading-[1.02] tracking-[-0.01em]">
              Private-market health, sector by sector.
            </h1>
            <p className="mt-6 max-w-2xl text-[15.5px] leading-[1.7] text-[#0B2E24]/75">
              A free monthly readout of median composite scores across every sector on VenturaZ — grounded in filings, disclosures and traction. Institutional readers save the PDF; founders benchmark against their peers.
            </p>
          </div>
          <div className="lg:col-span-4">
            <button
              type="button"
              onClick={() => setGateOpen(true)}
              className="inline-flex items-center gap-2 px-5 py-3 text-[13px] font-heading font-semibold uppercase tracking-[0.16em] rounded-sm text-[#0B2E24] transition-colors shadow-[0_10px_28px_-14px_rgba(196,247,81,0.75)]"
              style={{ background: LIME }}
              data-testid="sector-reports-request-btn"
            >
              <Mail size={16} strokeWidth={2.25} />
              Email me the {monthLabel || "current month"} PDF
            </button>
          </div>
        </div>
      </section>

      {/* Table */}
      <section className="py-24" style={{ background: IVORY_2 }}>
        <div className="max-w-6xl mx-auto px-6">
          {loading ? (
            <div className="flex items-center gap-2 text-[#0B2E24]/60 text-sm justify-center py-16">
              <Loader2 size={16} className="animate-spin" /> Computing sector medians…
            </div>
          ) : !data || (data.rows || []).length === 0 ? (
            <div className="text-center py-16 text-[#0B2E24]/70" data-testid="sector-reports-empty">
              <div className="font-editorial text-[28px] leading-tight mb-2">Report will publish soon.</div>
              <div className="text-sm">Not enough live evaluations this month yet. Check back next week.</div>
            </div>
          ) : (
            <div
              className="rounded-md border overflow-hidden"
              style={{ borderColor: "rgba(11,46,36,0.15)", background: IVORY }}
              data-testid="sector-reports-table"
            >
              <div
                className="grid grid-cols-12 px-6 py-4 text-[11px] font-mono uppercase tracking-widest"
                style={{ background: FOREST, color: IVORY }}
              >
                <div className="col-span-5">Sector</div>
                <div className="col-span-3 text-center">Median score</div>
                <div className="col-span-2 text-center">Sample</div>
                <div className="col-span-1 text-center">Top</div>
                <div className="col-span-1 text-center">Bottom</div>
              </div>
              {data.rows.map((r, i) => (
                <div
                  key={r.sector}
                  className={`grid grid-cols-12 px-6 py-5 items-center border-t border-[#0B2E24]/10 ${i % 2 ? "bg-white/40" : ""}`}
                  data-testid={`sector-row-${r.sector.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div className="col-span-5">
                    <div className="font-editorial text-[22px] leading-tight text-[#0B2E24]">{r.sector}</div>
                  </div>
                  <div className="col-span-3 text-center">
                    <span className="inline-flex items-baseline gap-1 font-editorial text-[32px] text-[#0B2E24]">
                      {r.median_score}
                      <span className="text-[13px] text-[#0B2E24]/45">/100</span>
                    </span>
                  </div>
                  <div className="col-span-2 text-center font-mono text-sm text-[#0B2E24]/70">{r.sample_size}</div>
                  <div className="col-span-1 text-center font-mono text-sm text-[#0B2E24]/70">{r.top_score}</div>
                  <div className="col-span-1 text-center font-mono text-sm text-[#0B2E24]/70">{r.bottom_score}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Methodology */}
      <section className="py-20" style={{ background: IVORY }}>
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-2 text-[#0B2E24]/70 text-[11px] font-mono uppercase tracking-[0.22em] mb-4">
            <TrendingUp size={13} /> METHODOLOGY
          </div>
          <h2 className="font-editorial text-[34px] leading-tight text-[#0B2E24] mb-6">
            How the composite score is built.
          </h2>
          <div className="grid md:grid-cols-2 gap-8 text-[14.5px] leading-[1.7] text-[#0B2E24]/78">
            <div>
              <div className="font-heading text-[#0B2E24] mb-2">Compliance · 30%</div>
              MCA filings cadence, GST currency, AGM timeliness, open charges.
            </div>
            <div>
              <div className="font-heading text-[#0B2E24] mb-2">Financials · 30%</div>
              Revenue quality, burn discipline, gross-margin trajectory, capital efficiency.
            </div>
            <div>
              <div className="font-heading text-[#0B2E24] mb-2">Governance · 20%</div>
              Board composition, cap-table clarity, related-party disclosures.
            </div>
            <div>
              <div className="font-heading text-[#0B2E24] mb-2">Traction · 20%</div>
              Customer traction, retention proxies, market signal.
            </div>
          </div>
        </div>
      </section>

      {/* Archive */}
      {data && (data.archive || []).length > 1 && (
        <section className="py-20 border-t" style={{ background: IVORY_2, borderColor: "rgba(11,46,36,0.08)" }} data-testid="sector-reports-archive">
          <div className="max-w-4xl mx-auto px-6">
            <div className="flex items-center gap-2 text-[#0B2E24]/70 text-[11px] font-mono uppercase tracking-[0.22em] mb-4">
              <TrendingUp size={13} /> ARCHIVE
            </div>
            <h2 className="font-editorial text-[32px] leading-tight text-[#0B2E24] mb-8">
              Previous months.
            </h2>
            <div className="grid gap-2">
              {(data.archive || []).filter((a) => a.month !== data.month).map((a) => {
                const label = new Date(`${a.month}-01`).toLocaleDateString(undefined, { month: "long", year: "numeric" });
                return (
                  <button
                    key={a.month}
                    type="button"
                    onClick={() => { setGateOpen(true); setForm((f) => ({ ...f, requested_month: a.month })); }}
                    data-testid={`sector-archive-row-${a.month}`}
                    className="flex items-center justify-between px-5 py-4 rounded-md border border-[#0B2E24]/12 hover:border-[#0B2E24] transition-colors bg-white text-left"
                  >
                    <div>
                      <div className="font-heading text-[#0B2E24] text-sm">{label}</div>
                      <div className="text-[11px] font-mono text-[#0B2E24]/55 mt-0.5">
                        Generated {a.generated_at ? formatDate(a.generated_at) : "—"}
                        {a.sample_total != null ? ` · ${a.sample_total} companies` : ""}
                      </div>
                    </div>
                    <div className="text-[11px] font-mono uppercase tracking-widest text-[#8A6600]">
                      Request PDF <Mail size={12} className="inline ml-1" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <Footer />

      {/* --- Email gate modal --- */}
      {gateOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: "rgba(11,46,36,0.55)" }}
          onClick={() => setGateOpen(false)}
          data-testid="sector-gate-backdrop"
        >
          <div
            className="w-full max-w-md rounded-md p-8 shadow-2xl"
            style={{ background: IVORY }}
            onClick={(e) => e.stopPropagation()}
            data-testid="sector-gate-modal"
          >
            {downloadUrl ? (
              <div className="text-center py-4" data-testid="sector-gate-success">
                <CheckCircle2 size={36} className="mx-auto text-emerald-600 mb-4" />
                <div className="font-editorial text-[26px] leading-tight text-[#0B2E24] mb-3">
                  Report on its way.
                </div>
                <div className="text-[13px] text-[#0B2E24]/70 mb-6">
                  We&apos;ve emailed the PDF to <span className="font-mono">{form.email}</span>. You can also download it now:
                </div>
                <a
                  href={downloadUrl}
                  download
                  className="inline-flex items-center gap-2 px-5 py-3 text-[13px] font-heading font-semibold uppercase tracking-[0.16em] text-[#F5F1E8] rounded-sm"
                  style={{ background: FOREST }}
                  data-testid="sector-gate-download-now"
                >
                  <Download size={14} /> Download PDF
                </a>
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={() => { setGateOpen(false); setDownloadUrl(null); setForm({ email: "", name: "", organization: "", role: "", requested_month: null }); }}
                    className="text-[11px] font-mono uppercase tracking-widest text-[#0B2E24]/60 hover:text-[#0B2E24]"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submitGate}>
                <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#0B2E24]/60 mb-2">FREE · MONTHLY REPORT</div>
                <div className="font-editorial text-[26px] leading-tight text-[#0B2E24] mb-2">
                  Get the {form.requested_month ? new Date(`${form.requested_month}-01`).toLocaleDateString(undefined, { month: "long", year: "numeric" }) : monthLabel || "current"} PDF.
                </div>
                <div className="text-[13px] text-[#0B2E24]/70 mb-5">
                  Where should we send it? We&apos;ll only email you when a new sector report drops.
                </div>
                <label className="block mb-3">
                  <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#0B2E24]/60 mb-1">Work email *</div>
                  <input
                    type="email"
                    required
                    autoFocus
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    className="w-full bg-white border border-[#0B2E24]/20 text-[#0B2E24] px-3 py-2.5 text-sm rounded focus:border-[#0B2E24] outline-none"
                    data-testid="sector-gate-email"
                    placeholder="you@fund.com"
                  />
                </label>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <label className="block">
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#0B2E24]/60 mb-1">Name</div>
                    <input
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full bg-white border border-[#0B2E24]/20 text-[#0B2E24] px-3 py-2.5 text-sm rounded focus:border-[#0B2E24] outline-none"
                      data-testid="sector-gate-name"
                    />
                  </label>
                  <label className="block">
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#0B2E24]/60 mb-1">Role</div>
                    <input
                      value={form.role}
                      onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                      className="w-full bg-white border border-[#0B2E24]/20 text-[#0B2E24] px-3 py-2.5 text-sm rounded focus:border-[#0B2E24] outline-none"
                      placeholder="Partner"
                      data-testid="sector-gate-role"
                    />
                  </label>
                </div>
                <label className="block mb-6">
                  <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#0B2E24]/60 mb-1">Firm / organisation</div>
                  <input
                    value={form.organization}
                    onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))}
                    className="w-full bg-white border border-[#0B2E24]/20 text-[#0B2E24] px-3 py-2.5 text-sm rounded focus:border-[#0B2E24] outline-none"
                    placeholder="e.g. Northstar Ventures"
                    data-testid="sector-gate-org"
                  />
                </label>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 text-[13px] font-heading font-semibold uppercase tracking-[0.16em] rounded-sm text-[#F5F1E8] transition-colors disabled:opacity-60"
                  style={{ background: FOREST }}
                  data-testid="sector-gate-submit"
                >
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  {submitting ? "Sending…" : "Email me the report"}
                </button>
                <button
                  type="button"
                  onClick={() => setGateOpen(false)}
                  className="w-full mt-3 text-[11px] font-mono uppercase tracking-widest text-[#0B2E24]/60 hover:text-[#0B2E24]"
                >
                  Cancel
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
